import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  BarChart as BarChartIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

const SuperAdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const loadData = async () => {
      await fetchStats();
      await fetchUsers();
    };
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStats = async () => {
    try {
      const res = await api.get('/api/superadmin/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
      } else {
        setError('Failed to fetch statistics. Please refresh the page.');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/api/superadmin/users');
      setUsers(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
      } else {
        setError('Failed to fetch users. Please refresh the page.');
      }
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/login');
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const res = await api.put(`/api/superadmin/users/${userId}/role`, { role: newRole });
      
      // Update user in state
      setUsers(users.map(user => 
        user._id === userId ? { ...user, role: res.data.role } : user
      ));
      
      // If we were viewing this user's details, update that too
      if (selectedUser && selectedUser._id === userId) {
        setSelectedUser({ ...selectedUser, role: res.data.role });
      }
    } catch (err) {
      console.error('Failed to update user role:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
      } else {
        setError('Failed to update user role.');
      }
    }
  };

  const handleRoleChange = (userId, newRole) => {
    updateUserRole(userId, newRole);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%' }}>
      {/* Navbar */}
      <AppBar position="sticky" sx={{ width: '100%' }}>
        <Toolbar sx={{ 
          minHeight: { xs: 48, sm: 64 },
          px: { xs: 1, sm: 2 }
        }}>
          <DashboardIcon sx={{ 
            mr: { xs: 0.5, sm: 2 }, 
            fontSize: { xs: 18, sm: 24 },
            display: { xs: 'none', sm: 'block' }
          }} />
          <Typography variant="h6" sx={{
            flexGrow: 1,
            fontSize: { xs: '0.9rem', sm: '1.25rem' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {isMobile ? 'Admin Dashboard' : 'Super Admin Dashboard'}
          </Typography>
          <Tooltip title="Logout">
            <IconButton color="inherit" onClick={handleLogout} sx={{ p: { xs: 0.5, sm: 1.5 } }}>
              <LogoutIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ 
        flex: 1, 
        width: '100%',
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 1, sm: 2 },
        maxWidth: '100vw',
        overflow: 'hidden'
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Stats Overview */}
        <Box sx={{ mb: { xs: 2, sm: 4 } }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' },
              mb: { xs: 1, sm: 2 }
            }}
          >
            Site Statistics
          </Typography>
          <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: { xs: 1, sm: 1.5, md: 2 },
                textAlign: 'center',
                minHeight: { xs: 90, sm: 120, md: 140 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <PeopleIcon sx={{
                  fontSize: { xs: 24, sm: 30, md: 40 },
                  color: 'primary.main',
                  mb: { xs: 0.5, sm: 1 }
                }} />
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.875rem', md: '1.25rem' },
                    lineHeight: 1.2,
                    mb: { xs: 0.5, sm: 1 }
                  }}
                >
                  {isMobile ? 'Users' : 'Total Users'}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2.125rem' },
                    fontWeight: 'bold'
                  }}
                >
                  {stats.userCount || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: { xs: 1, sm: 1.5, md: 2 },
                textAlign: 'center',
                minHeight: { xs: 90, sm: 120, md: 140 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <BarChartIcon sx={{
                  fontSize: { xs: 24, sm: 30, md: 40 },
                  color: 'success.main',
                  mb: { xs: 0.5, sm: 1 }
                }} />
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.875rem', md: '1.25rem' },
                    lineHeight: 1.2,
                    mb: { xs: 0.5, sm: 1 }
                  }}
                >
                  {isMobile ? 'Trades' : 'Total Trades'}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2.125rem' },
                    fontWeight: 'bold'
                  }}
                >
                  {stats.tradeCount || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: { xs: 1, sm: 1.5, md: 2 },
                textAlign: 'center',
                minHeight: { xs: 90, sm: 120, md: 140 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <AccountBalanceIcon sx={{
                  fontSize: { xs: 24, sm: 30, md: 40 },
                  color: 'secondary.main',
                  mb: { xs: 0.5, sm: 1 }
                }} />
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.875rem', md: '1.25rem' },
                    lineHeight: 1.2,
                    mb: { xs: 0.5, sm: 1 }
                  }}
                >
                  {isMobile ? 'P&L' : 'Total P&L'}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2.125rem' },
                    fontWeight: 'bold',
                    color: (stats.totalPnl || 0) >= 0 ? 'success.main' : 'error.main'
                  }}
                >
                  ${(stats.totalPnl || 0).toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Paper sx={{
                p: { xs: 1, sm: 1.5, md: 2 },
                textAlign: 'center',
                minHeight: { xs: 90, sm: 120, md: 140 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <TrendingUpIcon sx={{
                  fontSize: { xs: 24, sm: 30, md: 40 },
                  color: 'info.main',
                  mb: { xs: 0.5, sm: 1 }
                }} />
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.875rem', md: '1.25rem' },
                    lineHeight: 1.2,
                    mb: { xs: 0.5, sm: 1 }
                  }}
                >
                  {isMobile ? 'Avg P&L' : 'Avg Trade P&L'}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2.125rem' },
                    fontWeight: 'bold',
                    color: (stats.avgPnl || 0) >= 0 ? 'success.main' : 'error.main'
                  }}
                >
                  ${(stats.avgPnl || 0).toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Recent Signups */}
        <Box sx={{ mb: { xs: 2, sm: 4 } }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              mb: { xs: 1, sm: 2 }
            }}
          >
            {isMobile ? 'Recent Signups' : 'Recent Signups (Last 30 Days)'}
          </Typography>
          <Paper sx={{ 
            p: { xs: 0.5, sm: 1, md: 2 }, 
            overflow: 'hidden',
            width: '100%'
          }}>
            {stats.recentSignups && stats.recentSignups.length > 0 ? (
              <Box sx={{ overflowX: 'auto', width: '100%' }}>
                <Table size="small" sx={{ minWidth: { xs: 280, sm: 400 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 1 }
                      }}>
                        Email
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 1 }
                      }}>
                        Role
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 1 }
                      }}>
                        Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.recentSignups.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                          py: { xs: 0.5, sm: 1 },
                          px: { xs: 0.5, sm: 1 }
                        }}>
                          <Box sx={{ 
                            wordBreak: 'break-all', 
                            maxWidth: { xs: 100, sm: 150, md: 'none' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {user.email}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                          py: { xs: 0.5, sm: 1 },
                          px: { xs: 0.5, sm: 1 }
                        }}>
                          {user.role}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                          py: { xs: 0.5, sm: 1 },
                          px: { xs: 0.5, sm: 1 }
                        }}>
                          {new Date(user.createdAt).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography sx={{ 
                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                p: { xs: 1, sm: 2 },
                textAlign: 'center'
              }}>
                No recent signups
              </Typography>
            )}
          </Paper>
        </Box>

        {/* Top Traders */}
        <Box sx={{ mb: { xs: 2, sm: 4 } }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              mb: { xs: 1, sm: 2 }
            }}
          >
            Top Traders
          </Typography>
          <Paper sx={{ 
            p: { xs: 0.5, sm: 1, md: 2 }, 
            overflow: 'hidden',
            width: '100%'
          }}>
            {stats.topTraders && stats.topTraders.length > 0 ? (
              <Box sx={{ overflowX: 'auto', width: '100%' }}>
                <Table size="small" sx={{ minWidth: { xs: 200, sm: 300 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 1 }
                      }}>
                        User
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 1 }
                      }}>
                        Trades
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.topTraders.map((trader, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                          py: { xs: 0.5, sm: 1 },
                          px: { xs: 0.5, sm: 1 }
                        }}>
                          <Box sx={{ 
                            wordBreak: 'break-all', 
                            maxWidth: { xs: 100, sm: 150, md: 'none' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {trader.user}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                          py: { xs: 0.5, sm: 1 },
                          px: { xs: 0.5, sm: 1 }
                        }}>
                          {trader.tradeCount}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography sx={{ 
                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                p: { xs: 1, sm: 2 },
                textAlign: 'center'
              }}>
                No trading data available
              </Typography>
            )}
          </Paper>
        </Box>

        {/* Daily Trades Chart */}
        <Box sx={{ mb: { xs: 2, sm: 4 } }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              mb: { xs: 1, sm: 2 }
            }}
          >
            Daily Trades
          </Typography>
          <Paper sx={{
            p: { xs: 0.5, sm: 1, md: 2 },
            overflow: 'hidden',
            width: '100%',
            height: { xs: 200, sm: 300, md: 400 }
          }}>
            {stats.dailyTrades && stats.dailyTrades.length > 0 ? (
              <Bar
                data={{
                  labels: stats.dailyTrades.map(item => item.date),
                  datasets: [{
                    label: 'Trades',
                    data: stats.dailyTrades.map(item => item.count),
                    backgroundColor: 'rgba(25, 118, 210, 0.5)',
                    borderColor: 'rgba(25, 118, 210, 1)',
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Number of Trades'
                      }
                    },
                    x: {
                      title: {
                        display: true,
                        text: 'Date'
                      }
                    }
                  }
                }}
              />
            ) : (
              <Typography sx={{
                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                p: { xs: 1, sm: 2 },
                textAlign: 'center'
              }}>
                No trading data available
              </Typography>
            )}
          </Paper>
        </Box>

        {/* Role Distribution Chart */}
        <Box sx={{ mb: { xs: 2, sm: 4 } }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              mb: { xs: 1, sm: 2 }
            }}
          >
            User Role Distribution
          </Typography>
          <Paper sx={{
            p: { xs: 0.5, sm: 1, md: 2 },
            overflow: 'hidden',
            width: '100%',
            height: { xs: 200, sm: 300, md: 400 }
          }}>
            {stats.roleDistribution && Object.keys(stats.roleDistribution).length > 0 ? (
              <Pie
                data={{
                  labels: Object.keys(stats.roleDistribution),
                  datasets: [{
                    data: Object.values(stats.roleDistribution),
                    backgroundColor: [
                      'rgba(255, 99, 132, 0.5)',
                      'rgba(54, 162, 235, 0.5)',
                      'rgba(255, 206, 86, 0.5)',
                      'rgba(75, 192, 192, 0.5)',
                      'rgba(153, 102, 255, 0.5)',
                    ],
                    borderColor: [
                      'rgba(255, 99, 132, 1)',
                      'rgba(54, 162, 235, 1)',
                      'rgba(255, 206, 86, 1)',
                      'rgba(75, 192, 192, 1)',
                      'rgba(153, 102, 255, 1)',
                    ],
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    }
                  }
                }}
              />
            ) : (
              <Typography sx={{
                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                p: { xs: 1, sm: 2 },
                textAlign: 'center'
              }}>
                No user data available
              </Typography>
            )}
          </Paper>
        </Box>

        {/* User Management */}
        <Box sx={{ mb: { xs: 2, sm: 4 } }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              mb: { xs: 1, sm: 2 }
            }}
          >
            User Management
          </Typography>
          <Paper sx={{ 
            p: { xs: 0.5, sm: 1, md: 2 }, 
            overflow: 'hidden',
            width: '100%'
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : users.length > 0 ? (
              <Box sx={{ overflowX: 'auto', width: '100%' }}>
                <Table size="small" sx={{ minWidth: { xs: 350, sm: 500 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 1 }
                      }}>
                        Email
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 1 }
                      }}>
                        Role
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                        py: { xs: 0.5, sm: 1 },
                        px: { xs: 0.5, sm: 1 }
                      }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                          py: { xs: 0.5, sm: 1 },
                          px: { xs: 0.5, sm: 1 }
                        }}>
                          <Box sx={{ 
                            wordBreak: 'break-all', 
                            maxWidth: { xs: 120, sm: 180, md: 'none' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {user.email}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          minWidth: { xs: 80, sm: 120 },
                          py: { xs: 0.5, sm: 1 },
                          px: { xs: 0.5, sm: 1 }
                        }}>
                          <FormControl fullWidth size="small">
                            <Select
                              value={user.role}
                              onChange={(e) => handleRoleChange(user._id, e.target.value)}
                              sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                                '& .MuiSelect-select': {
                                  py: { xs: 0.5, sm: 1 }
                                }
                              }}
                            >
                              <MenuItem value="user" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                                User
                              </MenuItem>
                              <MenuItem value="superadmin" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                                {isMobile ? 'Admin' : 'Super Admin'}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell sx={{ 
                          py: { xs: 0.5, sm: 1 },
                          px: { xs: 0.5, sm: 1 }
                        }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setSelectedUser(user)}
                            sx={{
                              fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.75rem' },
                              minWidth: { xs: 50, sm: 80 },
                              px: { xs: 0.5, sm: 1, md: 2 },
                              py: { xs: 0.25, sm: 0.5 }
                            }}
                          >
                            {isMobile ? 'View' : 'View Details'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography sx={{ 
                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                p: { xs: 1, sm: 2 },
                textAlign: 'center'
              }}>
                No users found
              </Typography>
            )}
          </Paper>
        </Box>

        {/* User Details Modal */}
        {selectedUser && (
          <Paper sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3,
            mx: { xs: 0, sm: 0 }
          }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              User Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, mb: 1 }}>
                  <strong>Email:</strong> {selectedUser.email}
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, mb: 1 }}>
                  <strong>Role:</strong> {selectedUser.role}
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, mb: 1 }}>
                  <strong>ID:</strong> {selectedUser._id}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, mb: 1 }}>
                  <strong>Created:</strong> {new Date(selectedUser.createdAt).toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, mb: 1 }}>
                  <strong>Updated:</strong> {new Date(selectedUser.updatedAt).toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setSelectedUser(null)}
                size={isMobile ? 'small' : 'medium'}
                sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}
              >
                Close
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: { xs: 2, sm: 3 },
          px: 2,
          mt: 'auto',
          backgroundColor: 'grey.200',
          textAlign: 'center',
        }}
      >
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
        >
          Super Admin Dashboard © 2025 | Built by ZenoLaunch Team
        </Typography>
      </Box>
    </Box>
  );
};


export default SuperAdminDashboard;
